import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import FormField from "@/components/ui/formField";

import { twMerge } from "tailwind-merge";

import { PASSWORD_FORM_COPY, PASSWORD_FORM_RULES } from "./data";
import { PasswordInputChecklist } from "./PasswordInputChecklist";
import { PasswordFormData, PasswordFormProps } from "./types";

export const PasswordForm = ({
  mode = "resetPassword",
  isSubmitting,
  onSubmit
}: PasswordFormProps): JSX.Element => {
  const formProviderProps = useForm<PasswordFormData>({
    mode: "onChange",
    defaultValues: { password: "", confirmPassword: "" }
  });
  const { register, formState, watch, trigger, handleSubmit } =
    formProviderProps;
  const disableButton = useMemo(
    () => isSubmitting || !formState.isValid,
    [isSubmitting, formState.isValid]
  );
  return (
    <FormProvider {...formProviderProps}>
      <form
        className="mt-8"
        onSubmit={handleSubmit(() => onSubmit?.(watch("password")))}
      >
        <div className="flex flex-col gap-6" data-testid="reset-password-form">
          <div className="flex flex-col">
            <h2 className="text-[32px] leading-[40px] font-semibold text-txtHeading">
              {PASSWORD_FORM_COPY.pageTitle[mode]}
            </h2>
            <p className="text-sm leading-[22px] text-txtHeading mt-1">
              Follow these requirements for a secure password:
            </p>
            <PasswordInputChecklist inputValue={watch("password")} />
          </div>
          <div className="flex flex-col gap-4">
            {/* The error of password input will be shown on checklist above. There will be no red error on input */}
            <FormField
              data-testid="reset-password-input"
              register={register}
              placeHolder="Password"
              type="password"
              name="password"
              rules={PASSWORD_FORM_RULES.password}
              onChange={() => {
                // need to check the matchness with confirm password too when confirm password is filled
                if (watch("confirmPassword")) {
                  trigger("confirmPassword");
                }
              }}
              autoCleanError={false}
              hasSpacingOnFocus={false}
              disabled={isSubmitting}
            />
            <FormField
              data-testid="reset-confirm-password-input"
              register={register}
              placeHolder="Confirm password"
              type="password"
              name="confirmPassword"
              rules={PASSWORD_FORM_RULES.confirmPassword}
              error={formState.errors.confirmPassword?.message}
              autoCleanError={false}
              hasSpacingOnFocus={false}
              disabled={isSubmitting}
            />
          </div>
          <Button
            type="submit"
            disabled={disableButton}
            className={twMerge(
              "rounded-xl font-semibold text-base py-3 mt-8",
              disableButton && "bg-gray-300 disabled:opacity-70"
            )}
          >
            {PASSWORD_FORM_COPY.submitButton[mode]}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default PasswordForm;
